import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useExpert } from '../../contexts/ExpertContext';
import { BrCard, BrButton } from '@govbr-ds/react-components';

export default function ExpertSummaryPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { state, endSession } = useExpert();

    useEffect(() => {
        if (!state.session || state.session.evaluations.length === 0) {
            navigate('/especialista');
        }
    }, [state.session, navigate]);

    if (!state.session) {
        return null;
    }

    const evaluations = state.session.evaluations;

    // Calculate average scores
    const avgScores = {
        clarity: evaluations.reduce((sum, e) => sum + e.ratings.clarity, 0) / evaluations.length,
        adequacy: evaluations.reduce((sum, e) => sum + e.ratings.adequacy, 0) / evaluations.length,
        consistency: evaluations.reduce((sum, e) => sum + e.ratings.consistency, 0) / evaluations.length,
        accessibility: evaluations.reduce((sum, e) => sum + e.ratings.accessibility, 0) / evaluations.length,
        usability: evaluations.reduce((sum, e) => sum + e.ratings.usability, 0) / evaluations.length
    };

    const overallAvg = Object.values(avgScores).reduce((a, b) => a + b, 0) / 5;

    // Group by persona
    const byPersona = evaluations.reduce((acc, e) => {
        if (!acc[e.profile_id]) acc[e.profile_id] = [];
        acc[e.profile_id].push(e);
        return acc;
    }, {} as Record<string, typeof evaluations>);

    const handleNewSession = () => {
        endSession();
        navigate('/especialista');
    };

    return (
        <div className="container-lg py-4">
            <div className="row justify-content-center">
                <div className="col-12 col-md-10 col-lg-8 d-flex flex-column gap-4">
                    <BrCard>
                        <div className="text-center mb-8">
                            <span className="text-5xl">🎉</span>
                            <h1 className="text-2xl font-bold text-gray-80 mt-4">
                                {t('expert.summaryPage.title')}
                            </h1>
                            <p className="text-gray-60 mt-2">
                                {t('expert.summaryPage.thankYouMessage')}
                            </p>
                        </div>

                        {/* Summary Card */}
                        <div className="bg-success border border-success rounded-lg p-3 mb-6" style={{ backgroundColor: 'rgba(22, 136, 33, 0.1)' }}>
                            <div className="d-flex flex-column flex-md-row align-items-center justify-content-around gap-3 text-center">
                                <div>
                                    <div className="text-xs text-gray-80">{t('expert.summaryPage.evaluated')}</div>
                                    <div className="text-2xl font-bold text-success">{evaluations.length}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-80">{t('expert.summaryPage.averageScore')}</div>
                                    <div className="text-2xl font-bold text-success">{overallAvg.toFixed(1)}/5</div>
                                </div>
                            </div>
                        </div>

                        {/* Dimension Averages */}
                        <div className="mb-6">
                            <h3 className="font-semibold text-gray-80 mb-3">{t('expert.summaryPage.dimensionAverages')}</h3>
                            <div className="d-flex flex-column gap-2">
                                {Object.entries(avgScores).map(([key, value]) => (
                                    <div key={key} className="d-flex align-items-center gap-3">
                                        <div className="w-25 text-sm text-gray-60 capitalize" style={{ width: '7rem' }}>{t(`expert.dimensions.${key}`)}</div>
                                        <div className="flex-1 h-4 bg-gray-20 rounded-full overflow-hidden" style={{ height: '1rem' }}>
                                            <div
                                                className="h-100 bg-primary-default transition-all"
                                                style={{ width: `${(value / 5) * 100}%` }}
                                            />
                                        </div>
                                        <div className="w-12 text-right font-medium text-gray-80">{value.toFixed(1)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* By Persona */}
                        <div className="mb-6">
                            <h3 className="font-semibold text-gray-80 mb-3">{t('expert.summaryPage.evaluationsByPersona')}</h3>
                            <div className="d-flex flex-wrap gap-3">
                                {Object.entries(byPersona).map(([persona, evals]) => {
                                    const avg = evals.reduce((sum, e) => {
                                        return sum + (e.ratings.clarity + e.ratings.adequacy + e.ratings.consistency +
                                            e.ratings.accessibility + e.ratings.usability) / 5;
                                    }, 0) / evals.length;
                                    return (
                                        <div key={persona} className="bg-gray-2 p-3 rounded-lg flex-1 text-center" style={{ minWidth: '45%' }}>
                                            <div className="font-medium text-gray-80">{t(`expert.personasPage.personas.${persona}.name`)}</div>
                                            <div className="text-xs text-gray-60">{evals.length} interfaces</div>
                                            <div className="text-lg font-bold text-primary-default">{avg.toFixed(1)}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="d-flex flex-column gap-3">
                            <BrButton
                                primary
                                onClick={handleNewSession}
                                className="w-full"
                                style={{ whiteSpace: 'normal', minHeight: '48px' }}
                                aria-label={t('expert.summaryPage.newEvaluation')}
                            >
                                {t('expert.summaryPage.newEvaluation')}
                            </BrButton>
                        </div>
                    </BrCard>
                </div>
            </div>
        </div>
    );
}
